import React, { useState, useEffect, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQuery } from '@apollo/client';

import { Container, DropdownFilter } from 'components';
import { authenticationState, loadingState } from 'state/atoms';
import { RMA_MANAGER_MANAGER } from 'connectors/permissions';
import { LIST_RMA_CASES } from 'connectors/queries/rmaCases';

import { applyFilters } from './filters';
import {
  ALL_CONTACTS_FILTER,
  VIEW_ALL_FILTER,
  ACTIVE_FILTER,
} from './constants';
import { getContactOptions, getStatusOptions } from './options';
import {
  AccountInformation,
  ResultsTable,
  Search,
  StartNewRma,
} from './subcomponents';
import { SearchWrapper, FiltersWrapper, Filter } from './styledComponents';

const getCaseListVariables = (authData, before) => {
  return {
    accountId: authData.endCustomerAccountId,
    input: {
      limit: 2000,
      ...(before ? { before } : {}),
    },
  };
};

const CaseManager = () => {
  const authData = useRecoilValue(authenticationState);
  const setLoading = useSetRecoilState(loadingState);

  const [results, setResults] = useState([]);
  const [contactFilter, setContactFilter] = useState(ALL_CONTACTS_FILTER);
  const [statusFilter, setStatusFilter] = useState(VIEW_ALL_FILTER);
  const [tabFilter, setTabFilter] = useState(ACTIVE_FILTER);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitedSearchTerm, setSubmitedSearchTerm] = useState('');
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(true);

  const { data, refetch } = useQuery(LIST_RMA_CASES, {
    variables: getCaseListVariables(authData),
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const dirtyResults = data.getAccount.listRmaCases.cases;
      const cleanResults = dirtyResults.filter((item) => item !== null);

      setResults([...results, ...cleanResults]);

      if (data.getAccount.listRmaCases.hasMore) {
        const lastResult = cleanResults[cleanResults.length - 1];
        refetch(getCaseListVariables(authData, lastResult.createdDate));
      } else {
        setIsBackgroundLoading(false);
      }
    },
  });

  useEffect(() => {
    setLoading(isBackgroundLoading && !data);
  }, [data, isBackgroundLoading, setLoading]);

  const filteredResults = useMemo(
    () =>
      applyFilters({
        items: results,
        term: submitedSearchTerm,
        contactFilter,
        statusFilter,
      }),
    [results, contactFilter, statusFilter, submitedSearchTerm]
  );

  const clearAllFilters = () => {
    setContactFilter(ALL_CONTACTS_FILTER);
    setStatusFilter(VIEW_ALL_FILTER);
    setTabFilter(ACTIVE_FILTER);
    setSubmitedSearchTerm('');
    setSearchTerm('');
  };

  return (
    <Container>
      <SearchWrapper>
        <AccountInformation {...authData} />
        <StartNewRma {...authData} />
        <Search
          rmaList={results}
          searchTerm={searchTerm}
          onChange={setSearchTerm}
          loading={isBackgroundLoading}
          onSubmit={(term) => {
            setSearchTerm(term);
            setSubmitedSearchTerm(term);
          }}
        />
        <FiltersWrapper>
          {authData &&
          authData.eCommerceAccess.includes(RMA_MANAGER_MANAGER) ? (
            <Filter>
              <label>Contact:</label>
              <DropdownFilter
                options={getContactOptions(
                  results,
                  authData.fullName,
                  contactFilter
                )}
                selectedOptionId={contactFilter}
                onSelect={(option) => setContactFilter(option.id)}
                loading={isBackgroundLoading}
              />
            </Filter>
          ) : null}
          <Filter>
            <label>Status:</label>
            <DropdownFilter
              options={getStatusOptions(results, statusFilter)}
              selectedOptionId={statusFilter}
              onSelect={(option) => setStatusFilter(option.id)}
              loading={isBackgroundLoading}
            />
          </Filter>
        </FiltersWrapper>
      </SearchWrapper>
      <ResultsTable
        items={filteredResults}
        onTabSelect={setTabFilter}
        tabFilter={tabFilter}
        allResultsCount={results.length}
        clearAllFilters={clearAllFilters}
        loading={isBackgroundLoading}
      />
    </Container>
  );
};

export { CaseManager };
